import { useEffect, useState } from 'react';
import { isValidUrl, checkPublicUrl, checkImageUrl, checkVideoUrl, checkAudioUrl } from '../utils/validators';

const TYPE_CHECKERS = {
  image: checkImageUrl,
  video: checkVideoUrl,
  audio: checkAudioUrl,
};

// Shared results for the statuses that need no network call. Returning the same object every time also means
// a consumer comparing results by identity sees no change while the status stays the same.
const IDLE     = { status: 'idle',     reason: null };
const INVALID  = { status: 'invalid',  reason: null };
const CHECKING = { status: 'checking', reason: null };

/**
 * Validates a URL asynchronously with a 600ms debounce.
 *
 * Routes to the correct checker based on `type`:
 *   'image' → checkImageUrl  (browser loads it as <img>)
 *   'video' → checkVideoUrl  (browser fetches video metadata)
 *   'audio' → checkAudioUrl  (browser fetches audio metadata)
 *   anything else → checkPublicUrl (reachability + not login-gated)
 *
 * When `type` changes (e.g. user switches media type from image to video),
 * the effect re-runs automatically and the correct checker is used.
 *
 * image/video/audio checkers resolve { ok, reason } so callers can show a
 * specific message (server unreachable vs. reachable-but-wrong-format)
 * instead of one generic "invalid" message. Other types' checkers still
 * resolve a plain boolean, so `reason` stays null for them.
 *
 * Only the RESULT of a finished check is kept as state. 'idle', 'invalid' and 'checking' are worked out while
 * rendering, from the url the hook was given — they never go through setState. An earlier version set
 * 'checking' from an effect on every change of the url, which re-rendered the field once more for every key
 * typed; typing fast enough (automation, 1–17 ms between keys) stacked those updates faster than React could
 * settle them, and React 19 gave up with error #185 and dropped keystrokes.
 *
 * A result is only shown for the exact url and type it was checked for; for any other url the status is
 * 'checking' until that url's own check finishes. That also means Save gating on "not checking" cannot
 * slip through while a newer url is still being checked.
 *
 * @param {string} url  - The URL to validate.
 * @param {string} type - 'image' | 'video' | 'audio' | 'hls' | '3d' | 'url'
 * @returns {{status: 'idle'|'checking'|'valid'|'invalid', reason: 'unreachable'|'invalid-format'|null}}
 */
const useUrlValidation = (url, type) => {
  // The last finished check: { url, type, result } — the only state this hook keeps.
  const [done, setDone] = useState(null);

  const hasUrl     = Boolean(url && url.trim());
  const wellFormed = hasUrl && isValidUrl(url);

  useEffect(() => {
    if (!wellFormed) return undefined;

    let cancelled = false;
    const checker = TYPE_CHECKERS[type] ?? checkPublicUrl;

    const debounce = setTimeout(async () => {
      const outcome = await checker(url);
      const isDetailed = outcome !== null && typeof outcome === 'object';
      const ok = isDetailed ? outcome.ok : outcome;
      const reason = isDetailed ? outcome.reason : null;
      if (!cancelled) {
        setDone({ url, type, result: { status: ok ? 'valid' : 'invalid', reason: ok ? null : reason } });
      }
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [url, type, wellFormed]);

  if (!hasUrl) return IDLE;
  if (!wellFormed) return INVALID;
  if (done && done.url === url && done.type === type) return done.result;
  return CHECKING;
};

export default useUrlValidation;
